import { BarChart, CheckCircle, Zap, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const CaseStudy = ({ 
  title, 
  company, 
  description, 
  metrics,
  pilotLabel,
}: { 
  title: string, 
  company: string, 
  description: string, 
  metrics: { icon: React.ElementType, label: string, value: string }[],
  pilotLabel: string,
}) => {
  return (
    <div className="soft-card group relative overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-blue-600 via-slate-600 to-emerald-500"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-slate-500/5 to-emerald-500/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative p-8">
        <div className="mb-6">
          <span className="mb-3 inline-flex rounded-full border border-blue-200/80 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-700 dark:border-blue-900/80 dark:bg-blue-950/40 dark:text-blue-300">
            {pilotLabel}
          </span>
          <p className="mb-2 text-sm font-medium text-blue-600 dark:text-blue-400">{company}</p>
          <h3 className="text-2xl font-semibold text-slate-900 transition-colors duration-300 group-hover:text-blue-600 dark:text-white dark:group-hover:text-blue-400">{title}</h3>
        </div>

        <p className="mb-8 line-clamp-3 text-slate-600 dark:text-slate-300">{description}</p>

        <div className="grid gap-4 sm:grid-cols-3">
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="flex flex-col items-center rounded-2xl border border-slate-200/70 bg-white/70 p-4 text-center shadow-sm backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
                <div className="mb-3 rounded-xl bg-gradient-to-br from-blue-100 via-slate-100 to-emerald-100 p-2 dark:from-blue-950/40 dark:via-slate-900/40 dark:to-emerald-950/40">
                  <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="mb-1 text-2xl font-semibold text-slate-900 dark:text-white">{metric.value}</span>
                <span className="text-sm text-slate-600 dark:text-slate-400">{metric.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CaseStudiesSection = () => {
  const { t } = useLanguage();
  
  const caseStudies = [
    {
      title: t("case_study_1_title"),
      company: t("case_study_1_subtitle"),
      description: t("case_study_1_desc"),
      metrics: [
        { icon: BarChart, label: t("metrics.efficiency_improvement"), value: "75%" },
        { icon: Clock, label: t("metrics.process_automation_rate"), value: "85%" },
        { icon: CheckCircle, label: t("metrics.error_reduction"), value: "40%" },
      ]
    },
    {
      title: t("case_study_2_title"),
      company: t("case_study_2_subtitle"),
      description: t("case_study_2_desc"),
      metrics: [
        { icon: BarChart, label: t("metrics.document_accuracy"), value: "89%" },
        { icon: Clock, label: t("metrics.processing_speed"), value: "2x faster" },
        { icon: BarChart, label: t("metrics.cost_savings"), value: "$10K/year" },
      ]
    },
    {
      title: t("case_study_3_title"),
      company: t("case_study_3_subtitle"),
      description: t("case_study_3_desc"),
      metrics: [
        { icon: BarChart, label: t("metrics.response_accuracy"), value: "92%" },
        { icon: Clock, label: t("metrics.response_time"), value: "<5s" },
        { icon: Zap, label: t("metrics.customer_satisfaction"), value: "4.8/5" },
      ]
    },
    {
      title: t("case_study_4_title"),
      company: t("case_study_4_subtitle"),
      description: t("case_study_4_desc"),
      metrics: [
        { icon: BarChart, label: t("metrics.data_processing_efficiency"), value: "85%" },
        { icon: BarChart, label: t("metrics.deployment_time"), value: "2 weeks" },
        { icon: CheckCircle, label: t("metrics.result_accuracy"), value: "97%" },
      ]
    }
  ];

  return (
    <section id="case-studies" className="relative overflow-hidden py-24 sm:py-28">
      <div className="absolute inset-0 z-0">
        <div className="absolute right-1/4 top-1/4 h-96 w-96 rounded-full bg-blue-500/8 blur-[120px] dark:bg-blue-600/10"></div>
        <div className="absolute bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-emerald-500/8 blur-[120px] dark:bg-emerald-600/10"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-14 text-center">
          <h2 className="section-heading mb-6">
            {t("case_studies_title")}
          </h2>
          <p className="section-subtitle mx-auto max-w-3xl">
            {t("case_studies_subtitle")}
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          {caseStudies.map((study, index) => (
            <CaseStudy
              key={index}
              title={study.title}
              company={study.company}
              description={study.description}
              metrics={study.metrics}
              pilotLabel={t("representative_pilot")}
            />
          ))}
        </div>

        <div className="mx-auto mt-8 max-w-4xl rounded-[1.5rem] border border-slate-200/70 bg-white/70 p-6 text-center shadow-sm backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
          <p className="text-sm leading-7 text-slate-600 dark:text-slate-400">
            {t("case_studies_note")}
          </p>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
